/**
 * MCQ Set Editor
 * Editor component for MCQ Question Set content type
 */

import * as React from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, cn } from '@ai-content-hub/content-kit';
import { generateId } from '@ai-content-hub/content-core';
import type { ElementRuntimeConfigSlice } from '@ai-content-hub/content-kit';
import { MCQEdit } from '../mcq/MCQEdit';
import { mcqElementSchema, getQuestionText } from '../mcq/schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import type { MCQElementData } from '../mcq/schema';
import { BlockEditor } from '../block';
import { createDefaultBlockContent } from '../block/types';
import type { BlockContent, BlockContentType } from '../block/types';
import { mcqSetSchema, type MCQSetData, type MCQSetItem, type MCQSetConfig } from './schema';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';

export interface MCQSetEditProps {
    /** MCQ Set data - passed by content-core */
    value: MCQSetData;
    /** Callback when data changes */
    onChange: (value: MCQSetData) => void;
    /** Config (optional) */
    config?: MCQSetConfig;
    /** Whether editing is disabled */
    disabled?: boolean;
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
    /** Additional class name */
    className?: string;
    /** Active content locale (from parent editor) */
    language?: string;
}

export const MCQSetEdit: React.FC<MCQSetEditProps> = ({
    value: data,
    onChange,
    config,
    disabled = false,
    errors,
    className,
    language,
}) => {
    const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());
    const { t, language: activeLanguage } = useElementSchemaLocale(mcqSetSchema.i18n, {
        config,
        language,
    });

    // Validate data
    const items = data?.items || [];

    // Update data
    const updateData = (updates: Partial<MCQSetData>) => {
        onChange({
            ...data,
            ...updates,
        });
    };

    // Toggle item expansion
    const toggleExpanded = (itemId: string) => {
        setExpandedItems((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    // Add new question
    const addQuestion = () => {
        if (disabled) return;
        const newItem: MCQSetItem = {
            id: generateId(),
            type: 'mcq',
            data: mcqElementSchema.defaultData as MCQElementData,
        };
        updateData({
            items: [...items, newItem],
        });
        // Auto-expand new item
        setExpandedItems((prev) => new Set(prev).add(newItem.id));
    };

    // Remove question
    const removeQuestion = (itemId: string) => {
        if (disabled) return;
        updateData({
            items: items.filter((item) => item.id !== itemId),
        });
    };

    // Update question
    const updateQuestion = (itemId: string, itemData: MCQElementData) => {
        if (disabled) return;
        updateData({
            items: items.map((item) => (item.id === itemId ? { ...item, data: itemData } : item)),
        });
    };

    // Move question up
    const moveUp = (index: number) => {
        if (disabled || index === 0) return;
        const newItems = [...items];
        [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
        updateData({ items: newItems });
    };

    // Move question down
    const moveDown = (index: number) => {
        if (disabled || index === items.length - 1) return;
        const newItems = [...items];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        updateData({ items: newItems });
    };

    // Get description content
    const descriptionContent: BlockContent = data?.description || {
        type: 'text' as const,
        text: '',
        variant: 'body' as const,
        alignment: 'left' as const,
    };

    // Handle description type change
    const handleDescriptionTypeChange = (type: BlockContentType) => {
        const currentType = data?.description?.type || 'text';
        if (currentType === type) return;
        const newContent = createDefaultBlockContent(type);
        updateData({ description: newContent });
    };

    // Handle description content change
    const handleDescriptionContentChange = (content: BlockContent) => {
        updateData({ description: content });
    };

    return (
        <div className={cn('mcq-set-edit space-y-6', className)}>
            {/* Set Header */}
            <Card className="mcq-set-edit-details shadow-sm gap-2 border-none rounded-b-none pt-0">
                <CardHeader className="mcq-set-edit-details-header px-0 pt-0">
                    <CardTitle className="mcq-set-edit-details-title text-lg">
                        {t('setDetailsTitle')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="mcq-set-edit-details-body space-y-4 px-0">
                    <div className="mcq-set-edit-description space-y-2">
                        <BlockEditor
                            layout="oneline"
                            content={descriptionContent}
                            onChange={handleDescriptionContentChange}
                            onTypeChange={handleDescriptionTypeChange}
                            disabled={disabled}
                            label={t('descriptionLabel')}
                            placeholder={t('enterDescription')}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Questions */}
            <div className="mcq-set-edit-questions">
                <div className="mcq-set-edit-questions-header">
                    <h3 className="mcq-set-edit-questions-title text-lg font-semibold">
                        {`${t('questionsListTitle')} (${items.length})`}
                    </h3>
                </div>

                {items.length === 0 ? (
                    <Card className="mcq-set-edit-empty border-dashed">
                        <CardContent className="mcq-set-edit-empty-body py-12 text-center">
                            <p className="mcq-set-edit-empty-text text-muted-foreground">
                                {t('emptyQuestionsMsg')}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="mcq-set-edit-question-list space-y-1">
                        {items.map((item, index) => {
                            const isExpanded = expandedItems.has(item.id);
                            const questionPreview =
                                getQuestionText(item.data) || t('untitledQuestion');
                            const itemHasNoCorrect = item.data?.choices?.length >= 2 && !item.data.choices.some((c) => c.isCorrect);
                            return (
                                <Card
                                    key={item.id}
                                    className="mcq-set-edit-question-item overflow-hidden border-0 shadow-sm gap-0 rounded-none [&:first-child]:rounded-t-lg [&:last-child]:rounded-b-lg"
                                >
                                    {/* Question Header */}
                                    <div
                                        className="mcq-set-edit-question-header flex items-center gap-2 p-3 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                                        onClick={() => toggleExpanded(item.id)}
                                    >
                                        <GripVertical className="mcq-set-edit-question-drag h-4 w-4 text-muted-foreground" />
                                        <span className="mcq-set-edit-question-number font-medium">
                                            Q{index + 1}
                                        </span>
                                        <span className="mcq-set-edit-question-preview flex-1 truncate text-sm text-muted-foreground">
                                            {questionPreview}
                                        </span>
                                        {itemHasNoCorrect && (
                                            <span
                                                className="mcq-set-edit-question-error-dot h-2 w-2 rounded-full bg-destructive shrink-0"
                                                title={t('selectCorrectAnswerError')}
                                            />
                                        )}
                                        <div className="mcq-set-edit-question-actions flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    moveUp(index);
                                                }}
                                                disabled={disabled || index === 0}
                                            >
                                                <ChevronUp className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    moveDown(index);
                                                }}
                                                disabled={disabled || index === items.length - 1}
                                            >
                                                <ChevronDown className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeQuestion(item.id);
                                                }}
                                                disabled={disabled}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Question Editor */}
                                    {isExpanded && (
                                        <CardContent className="mcq-set-edit-question-body px-0">
                                            <MCQEdit
                                                value={item.data}
                                                onChange={(newData) =>
                                                    updateQuestion(item.id, newData)
                                                }
                                                disabled={disabled}
                                                config={config}
                                                language={activeLanguage}
                                            />
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
                <div className="mcq-set-edit-add-question-row mt-4 flex">
                    <Button
                        onClick={addQuestion}
                        size="default"
                        variant="default"
                        disabled={disabled}
                        className="mcq-set-edit-add-question-button font-semibold shadow-sm"
                    >
                        <Plus className="h-4 w-4 shrink-0" />
                        {t('addQuestion')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MCQSetEdit;
